import { useQuery } from '@apollo/client';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as R from 'ramda';
import { Controller, useFormContext } from 'react-hook-form';
import { graphql } from '~/apollo/generated/v4';
import { DiagramAreaLinkType } from '~/apollo/generated/v4/graphql';
import { FormLabel } from '~/components/ui/forms/form-label';
import type { SelectOption } from '~/components/ui/forms/select';
import { Select } from '~/components/ui/forms/select';
import { TextInput } from '~/components/ui/forms/text-input';
import type { DiagramAreaFormValues } from '~/utils/modules/diagram';
import { snakeCapsToHuman } from '~/utils/text';

export function DiagramAreaFormFields() {
  const { register, control, watch } = useFormContext<DiagramAreaFormValues>();
  const targetOptions = useDiagramLinkTargetOptions();

  const linkTargetType = watch('linkTargetType');
  const linkTargetOptionsForType = targetOptions[linkTargetType];

  const numVertices = watch('path').length;

  return (
    <div className="space-y-2">
      <TextInput {...register('label')} label="Label" required />

      <Select
        {...register('linkTargetType')}
        label="Link type"
        required
        options={Object.values(DiagramAreaLinkType).map(t => ({
          value: t,
          label: snakeCapsToHuman(t),
        }))}
      />

      <Controller
        control={control}
        name="linkTargetId"
        render={({ field }) => (
          <Select
            {...field}
            label="Link target"
            required
            options={linkTargetOptionsForType}
          />
        )}
      />

      <div>
        <FormLabel label="Polygon" required />
        <div className="text-center p-4 border border-slate-200 rounded-lg">
          {pathLengthMessage(numVertices)}
        </div>
      </div>
    </div>
  );
}

function pathLengthMessage(numVertices: number) {
  if (numVertices === 0) {
    return 'Click on the image to start drawing a polygon.';
  }

  if (numVertices > 0 && numVertices < 3) {
    return 'Set at least three points to continue.';
  }

  return (
    <>
      <FontAwesomeIcon icon={faCheck} className="text-success" /> Coordinates
      set. Continue adding as many points as needed.
    </>
  );
}

const DIAGRAM_LINK_TARGET_OPTIONS = graphql(`
  query DiagramTargetFieldOptions {
    depositionalWikiList {
      results {
        id
        title
      }
    }
    formationList {
      results {
        id
        name
        wikiPageId
      }
    }
    groupList {
      results {
        id
        name
        wikiPageId
      }
    }
    memberList {
      results {
        id
        name
        wikiPageId
      }
    }
  }
`);

export function useDiagramLinkTargetOptions() {
  const { data } = useQuery(DIAGRAM_LINK_TARGET_OPTIONS);

  const mapLithostratWikiOptions = (item: {
    id: string;
    name: string;
    wikiPageId?: number | null;
  }): SelectOption => {
    const disabled = !item.wikiPageId;
    return {
      value: item.id,
      label: `${item.name}${disabled ? ' (no wiki page)' : ''}`,
      disabled,
    };
  };

  const options: Record<DiagramAreaLinkType, SelectOption[]> = {
    [DiagramAreaLinkType.GeologyWiki]:
      data?.depositionalWikiList?.results
        ?.slice()
        .sort(R.ascend(x => x.title))
        .map(gw => ({
          value: gw.id,
          label: gw.title,
        })) ?? [],
    [DiagramAreaLinkType.Formation]:
      data?.formationList?.results
        ?.slice()
        .sort(R.ascend(x => x.name))
        .map(fm => mapLithostratWikiOptions(fm)) ?? [],
    [DiagramAreaLinkType.Group]:
      data?.groupList?.results
        ?.slice()
        .sort(R.ascend(x => x.name))
        .map(group => mapLithostratWikiOptions(group)) ?? [],
    [DiagramAreaLinkType.Member]:
      data?.memberList?.results
        ?.slice()
        .sort(R.ascend(x => x.name))
        .map(mb => mapLithostratWikiOptions(mb)) ?? [],
  };

  return options;
}
